import React, { useEffect, useState } from "react";
import {
    Button,
    Card,
    Col,
    DatePicker,
    Form,
    Input,
    InputNumber,
    Row,
    Select,
    message,
} from "antd";
import dayjs from "dayjs";
import { createCustomerTransactionSchedule } from "../../api/customerTransactionApi";
import { getCustomerInstitutions } from "../../api/authApi";
import PdfUploadField from "./PdfUploadField";

const currencyOptions = [
    { label: "USD", value: "USD" },
    { label: "CNY", value: "CNY" },
    { label: "RUB", value: "RUB" },
    { label: "MNT", value: "MNT" },
    { label: "EUR", value: "EUR" },
];

export default function CustomerTransactionForm({ onSaved }) {
    const [form] = Form.useForm();
    const [invoiceFile, setInvoiceFile] = useState(null);
    const [contractFile, setContractFile] = useState(null);
    const [institutions, setInstitutions] = useState([]);
    const [loadingInstitutions, setLoadingInstitutions] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                setLoadingInstitutions(true);
                const data = await getCustomerInstitutions();
                setInstitutions(data || []);
            } catch (error) {
                message.error(error.message || "Failed to load institutions");
            } finally {
                setLoadingInstitutions(false);
            }
        };

        load();

        form.setFieldsValue({
            registrationDateTime: dayjs(),
            requestDateTime: dayjs(),
        });
    }, [form]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            if (!invoiceFile) {
                message.error("Invoice PDF is required");
                return;
            }

            if (!contractFile) {
                message.error("Contract PDF is required");
                return;
            }

            const payload = {
                requestNumber: values.requestNumber,
                institutionId: values.institutionId,
                registrationDateTime: values.registrationDateTime.format("YYYY-MM-DDTHH:mm:ss"),
                requestDateTime: values.requestDateTime.format("YYYY-MM-DDTHH:mm:ss"),
                currency: values.currency,
                paymentAmount: values.paymentAmount,
                beneficiaryCompany: values.beneficiaryCompany,
                beneficiaryBank: values.beneficiaryBank,
                beneficiaryAccount: values.beneficiaryAccount,
                paymentPurpose: values.paymentPurpose,
                invoiceFile,
                contractFile,
            };

            setSaving(true);
            await createCustomerTransactionSchedule(payload);
            message.success("Request submitted successfully");

            form.resetFields();
            setInvoiceFile(null);
            setContractFile(null);

            form.setFieldsValue({
                registrationDateTime: dayjs(),
                requestDateTime: dayjs(),
            });

            onSaved?.();
        } catch (error) {
            if (error?.errorFields) return;
            message.error(error.message || "Failed to submit request");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card title="New Transaction Request">
            <Form form={form} layout="vertical">
                <Row gutter={16}>
                    <Col xs={24} md={8}>
                        <Form.Item
                            name="requestNumber"
                            label="Request Number"
                            rules={[{ required: true, message: "Request number is required" }]}
                        >
                            <Input />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={8}>
                        <Form.Item
                            name="institutionId"
                            label="Institution"
                            rules={[{ required: true, message: "Institution is required" }]}
                        >
                            <Select
                                showSearch
                                loading={loadingInstitutions}
                                optionFilterProp="label"
                                options={(institutions || []).map((item) => ({
                                    label: item.name,
                                    value: item.id,
                                }))}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={8}>
                        <Form.Item
                            name="currency"
                            label="Currency"
                            rules={[{ required: true, message: "Currency is required" }]}
                        >
                            <Select options={currencyOptions} />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={8}>
                        <Form.Item
                            name="registrationDateTime"
                            label="Registration Datetime"
                            rules={[{ required: true, message: "Registration datetime is required" }]}
                        >
                            <DatePicker
                                showTime
                                style={{ width: "100%" }}
                                format="YYYY-MM-DD HH:mm:ss"
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={8}>
                        <Form.Item
                            name="requestDateTime"
                            label="Request Datetime"
                            rules={[{ required: true, message: "Request datetime is required" }]}
                        >
                            <DatePicker
                                showTime
                                style={{ width: "100%" }}
                                format="YYYY-MM-DD HH:mm:ss"
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={8}>
                        <Form.Item
                            name="paymentAmount"
                            label="Payment Amount"
                            rules={[{ required: true, message: "Payment amount is required" }]}
                        >
                            <InputNumber
                                min={0.01}
                                step={0.01}
                                precision={2}
                                style={{ width: "100%" }}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item
                            name="beneficiaryCompany"
                            label="Beneficiary Company"
                            rules={[{ required: true, message: "Beneficiary company is required" }]}
                        >
                            <Input />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item
                            name="beneficiaryBank"
                            label="Beneficiary Bank"
                            rules={[{ required: true, message: "Beneficiary bank is required" }]}
                        >
                            <Input />
                        </Form.Item>
                    </Col>

                    <Col xs={24}>
                        <Form.Item
                            name="beneficiaryAccount"
                            label="Beneficiary Account"
                            rules={[{ required: true, message: "Beneficiary account is required" }]}
                        >
                            <Input />
                        </Form.Item>
                    </Col>

                    <Col xs={24}>
                        <Form.Item
                            name="paymentPurpose"
                            label="Payment Purpose"
                            rules={[{ required: true, message: "Payment purpose is required" }]}
                        >
                            <Input.TextArea rows={4} />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <PdfUploadField
                            label="Invoice PDF"
                            file={invoiceFile}
                            setFile={setInvoiceFile}
                        />
                    </Col>

                    <Col xs={24} md={12}>
                        <PdfUploadField
                            label="Contract PDF"
                            file={contractFile}
                            setFile={setContractFile}
                        />
                    </Col>
                </Row>

                <div style={{ marginTop: 24 }}>
                    <Button type="primary" loading={saving} onClick={handleSubmit}>
                        Submit Request
                    </Button>
                </div>
            </Form>
        </Card>
    );
}